/**
 * IMPORTS
 */

/**
 * Hook React Query — POST `users/cliente-completo`
 * Cria cliente principal + endereço + usuários do cliente em uma única requisição.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { ICreateClientResponse } from "@/data/dtos/response-api/users/post-create-client-complet-response-api";
import type { ICreateClientPayload } from "@/data/dtos/users/post-create-client-complet-dto";
import { makePostCreateClientCompletUseCase } from "@/factories/users/make-crate-client-complet-factories";
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError, notifySuccess } from "@/utils/notify-messages";

/**
 * Mutation para criar cliente completo (backend agregado).
 *
 * @example
 * ```tsx
 * const { criarClienteCompletoAsync, isPending } = usePostCreateClienteCompleto();
 * await criarClienteCompletoAsync(meuPayload);
 * ```
 */
export function usePostCreateClienteCompleto() {
  const queryClient = useQueryClient();

  const mutation = useMutation<ICreateClientResponse, Error, ICreateClientPayload>({
    mutationFn: (payload) => makePostCreateClientCompletUseCase().execute(payload),

    onSuccess: async (response) => {
      if (response?.data?.user?.id) {
        notifySuccess(response.message ?? "Cliente criado com sucesso!");
        await queryClient.invalidateQueries({ queryKey: ["clientes"] });
        await queryClient.invalidateQueries({ queryKey: ["usuario-clientes"] });
        return;
      }
      notifyError("Resposta inesperada ao criar cliente.");
    },

    onError: (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });

  return {
    criarClienteCompleto: mutation.mutate,
    criarClienteCompletoAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
